import { graphql } from 'react-relay';
import React, { memo, useState, useCallback, useContext, useRef, useMemo, useEffect } from 'react';
import { css } from '@compiled/react';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl';
import type { MutationUpdaterFn } from 'apollo-client';

import { ChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { AnnotationInfo } from '@atlaskit/editor-plugin-annotation/types';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';
import Button from '@atlaskit/button/default/button';
import type { AnnotationUpdateEmitter } from '@atlaskit/editor-common/annotation';
import type { DocNode } from '@atlaskit/adf-schema/doc';

import type { MutationFunctionInteropSupportedOptions } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { CommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor.ts';
import { NewReplyContainer } from '@confluence/inline-comments-common/entry-points/styled.ts';
import type { CommentLocation } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import {
	useCommentsDataActions,
	CommentType,
	type CommentData,
	type ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import {
	useCommentsContentActions,
	useCommentsContentState,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { handleAgentMentionInComment } from '@confluence/agent-mention/entry-points/handleAgentMentionInComment.ts';
import { useAgentMentionFyiNudge } from '@confluence/agent-mention/entry-points/useAgentMentionFyiNudge.ts';
import { AgentMentionFyiNudgeAsync as AgentMentionFyiNudge } from '@confluence/agent-mention/entry-points/AgentMentionFyiNudgeAsync.ts';
import { useAllCommentsData } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useCreateObjectCommentMutation } from '@confluence/comments-data-manager/entry-points/useCreateObjectCommentMutation.ts';
import { useCreateInlineReplyMutation } from '@confluence/comments-data-manager/entry-points/useCreateInlineReplyMutation.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useContentType } from '@confluence/page-context';
import { isCanvasContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import {
	REPLY_TO_PAGE_COMMENT_LOAD_EXPERIENCE,
	REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import type { PageInfoNode } from '@confluence/page-info/entry-points/PageInfoQuery.ts';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import {
	getValidDate,
	formatTimestampForAriaLabel,
} from '@confluence/comments-util/entry-points/dateUtils.ts';
import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useAgentSessionsContext } from '@confluence/agent-mention/entry-points/AgentSessionsContext.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { WithCurvedBranchingStyle } from '@confluence/inline-comments-common/entry-points/components.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState.ts';
import { RepliesToggler } from '@confluence/comments-common/entry-points/RepliesToggler.ts';
import { RepliesFooter } from '@confluence/comments-common/entry-points/RepliesFooter.ts';
import {
	ViewReplyOptions,
	getCommentReplies,
} from '@confluence/comments-common/entry-points/repliesHelpers.ts';
import { useUpdateVisibleReplies } from '@confluence/comments-common/entry-points/useUpdateVisibleReplies.ts';
import { useVisibleReplies } from '@confluence/comments-common/entry-points/useVisibleReplies.ts';
import { GeneralReplyScroller } from '@confluence/comments-common/entry-points/GeneralReplyScroller.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { useCommentsPanel } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import { buildMutationContextForNoNetworkError } from '@confluence/comments-util/entry-points/errorUtils.ts';
import { useAnnotationsIsOffline } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import {
	onSaveReply,
	getReplyEditorContent,
	getDefaultNumVisibleReplies,
	getDefaultReplyView,
	type SaveProps,
} from '../helper/commentThreadHelper.tsx';
import { arePropsShallowEqualWhenMemoRowsEnabled } from '../helper/memoRowsExperiment.ts';

import type {
	CommentRepliesCreateGeneralReplyMutation as CommentRepliesCreateGeneralReplyMutation$data,
	CommentRepliesCreateGeneralReplyMutationVariables as CommentRepliesCreateGeneralReplyMutation$variables,
} from './__types__/CommentRepliesCreateGeneralReplyMutation.ts';
import type {
	CommentRepliesCreateInlineReplyMutation as CommentRepliesCreateInlineReplyMutation$data,
	CommentRepliesCreateInlineReplyMutationVariables as CommentRepliesCreateInlineReplyMutation$variables,
} from './__types__/CommentRepliesCreateInlineReplyMutation.ts';
import { Comment } from './Comment.tsx';

const CommentRepliesCreateInlineReplyMutation = graphql`
	mutation CommentRepliesCreateInlineReplyMutation(
		$input: ReplyInlineCommentInput!
		$pageId: ID!
		$includeReactions: Boolean!
	) {
		replyInlineComment(input: $input) {
			id
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				webui
				editui
			}
			location {
				type
				... on InlineComment {
					inlineMarkerRef
					inlineText
					inlineResolveProperties {
						resolved
						isDangling
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
				}
				... on FooterComment {
					commentResolveProperties {
						resolved
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			version {
				by {
					displayName
				}
				when
				number
			}
			author {
				displayName
				profilePicture {
					path
				}
				permissionType
				... on KnownUser {
					accountId
				}
			}
			createdAt {
				value
			}
			createdAtNonLocalized
			reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment")
				@include(if: $includeReactions) {
				ari
				containerAri
				reactionsCount
				reactionsSummaryForEmoji {
					id
					emojiId
					count
					reacted
				}
			}
		}
	}
`;
const CommentRepliesCreateGeneralReplyMutation = graphql`
	mutation CommentRepliesCreateGeneralReplyMutation(
		$pageId: ID!
		$cloudId: ID!
		$input: CreateCommentInput!
		$includeReactions: Boolean!
	) {
		createFooterComment(cloudId: $cloudId, input: $input) {
			id
			author {
				displayName
				profilePicture {
					path
				}
				permissionType
				... on KnownUser {
					accountId
				}
			}
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				webui
				editui
			}
			location {
				type
				... on InlineComment {
					inlineMarkerRef
					inlineText
					inlineResolveProperties {
						isDangling
						resolvedTime
						resolvedFriendlyDate
						resolvedUser
						resolved
					}
				}
				... on FooterComment {
					commentResolveProperties {
						resolvedTime
						resolvedFriendlyDate
						resolvedUser
						resolved
					}
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment")
				@include(if: $includeReactions) {
				ari
				containerAri
				reactionsCount
				reactionsSummaryForEmoji {
					id
					emojiId
					count
					reacted
				}
			}
			version {
				by {
					displayName
				}
				when
				number
			}
		}
	}
`;
const commentThreadRepliesBoxStyles = css({
	display: 'flex',
	flexDirection: 'column',
	paddingLeft: token('space.200'),
	paddingRight: token('space.100'),
});

const commentThreadRepliesBoxStylesV2 = css({
	paddingLeft: token('space.150'),
});

const commentThreadRepliesBoxStandaloneStyleOverrides = css({
	paddingRight: token('space.200'),
});

const commentThreadRepliesBoxStandaloneStyleOverridesV2 = css({
	paddingRight: token('space.150'),
});

const commentHandlesPaddingReplyBoxOverrides = css({
	paddingTop: token('space.0'),
	paddingLeft: token('space.0'),
	paddingBottom: token('space.0'),
});

const replyBoxStyles = css({
	listStyle: 'none',
	position: 'relative',
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.300'),
});

const translucentStyle = css({
	opacity: '50%',
});

const replyActionButtonStyles = {
	marginLeft: token('space.150'),
	width: '62px',
	height: '24px',
	left: '-12px',
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
};

const customizedReplyButtonStyles = css({
	'&::after': {
		borderStyle: 'none',
	},
});

// This would cause the branching style to jump when clicking reply
const commentEditorLegacyCustomStyles = {
	marginLeft: token('space.300'),
	height: '16px',
	marginRight: token('space.050'),
};

const commentEditorCustomStyles = {
	marginLeft: token('space.300'),
	height: '24px', // 24px = 16px offset + 8px of padding
	marginRight: token('space.050'),
	paddingTop: token('space.100'),
};

export type CommentRepliesProps = {
	parentComment: CommentData;
	pageInfo: PageInfoNode | null;
	hoveredCommentId: string;
	pageMode: PageMode;
	editCommentQueryId?: string;
	selectedAnnotation: AnnotationInfo;
	parentCommentId: string;
	handleMouseEnter: (id?: string) => void;
	handleMouseLeave: () => void;
	canAddComments: boolean;
	isStandaloneComment?: boolean;
	deleteStandaloneComment?: (parentCommentId: string, commentId: string) => void;
	replyToComment?: (commentId: string, commentData: ReplyData) => void;
	updateReactionsCacheFn?: (
		emojiId: string,
		actionType: 'add' | 'delete',
		contentId: string,
		containerId?: string,
	) => MutationUpdaterFn<any> | void;
	onRendered?: () => void;
	eventEmitter?: AnnotationUpdateEmitter;
	isEditorExpanded?: boolean;
	hasParentMaxHeight?: boolean;
	flags?: FlagsStateContainer;
	hideReactions?: boolean;
	/** True only when rendered inside the comment summary popup (via CommentThread). */
	isCommentSummaryPopup?: boolean;
};

const i18n = defineMessages({
	replyAction: {
		id: 'comments-panel.reply.action',
		defaultMessage: 'Reply',
		description: 'Reply button label for comment thread in comments panel',
	},
	threadReplyButtonAriaLabelWithName: {
		id: 'comments-panel.reply.button.aria-label.with-name',
		defaultMessage: 'Reply to {name}’s thread',
		description: 'Reply placeholder for comment thread, with the first comment author name',
	},
	replyAriaLabel: {
		id: 'comments-panel.reply.aria-label',
		defaultMessage: 'Reply by {authorName}, {timestamp}',
		description: 'Aria label for reply container announcing the author and timestamp',
	},
});

const CommentRepliesComponent = ({
	parentComment,
	pageInfo,
	hoveredCommentId,
	pageMode,
	editCommentQueryId,
	selectedAnnotation,
	parentCommentId,
	handleMouseEnter,
	handleMouseLeave,
	canAddComments,
	isStandaloneComment,
	deleteStandaloneComment,
	replyToComment,
	updateReactionsCacheFn,
	onRendered,
	eventEmitter,
	isEditorExpanded = false,
	hasParentMaxHeight = false,
	flags,
	hideReactions = false,
	isCommentSummaryPopup = false,
}: CommentRepliesProps): React.JSX.Element => {
	const [createInlineReplyFn] = useMutationInterop<
		CommentRepliesCreateInlineReplyMutation$data,
		CommentRepliesCreateInlineReplyMutation$variables
	>(CommentRepliesCreateInlineReplyMutation, buildMutationContextForNoNetworkError());
	const [createGeneralReplyFn] = useMutationInterop<
		CommentRepliesCreateGeneralReplyMutation$data,
		CommentRepliesCreateGeneralReplyMutation$variables
	>(CommentRepliesCreateGeneralReplyMutation, buildMutationContextForNoNetworkError());
	const { isReactionsEnabled } = useContext(ReactionsContext);
	// A reply renders its agent-session status row below itself; read the page's agent-session
	// map so we can keep the connector line visible for such a reply (mirrors the footer's
	// ObjectCommentNestableReplies), otherwise the status floats when the reply is last.
	const agentSessions = useAgentSessionsContext();

	const pageId = pageInfo?.id ?? '';
	const pageType = pageInfo?.type ?? '';

	const { createInlineReplyInterop } = useCreateInlineReplyMutation({ contentId: pageId });

	const { addCommentInCache } = useAllCommentsData({
		contentId: pageId,
		options: {
			skip: true, // Only using write operations, no need to fetch data
		},
	});
	const createObjectCommentMutationFn = useCreateObjectCommentMutation();

	const intl = useIntl();
	const { formatMessage } = intl;

	const { hasContentChanged } = useCommentsContentState();
	const { onChange, resetContentChanged } = useCommentsContentActions();
	const { cloudId, userId, displayName } = useSessionData();
	const {
		showFyi,
		agent,
		onContentChange: onAgentMentionContentChange,
	} = useAgentMentionFyiNudge({
		cloudId,
		enabled: expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true),
	});

	// Track whether the FYI component has ever been shown, so we only load the
	// chunk on first mention but keep it mounted for the collapse animation.
	const hasShownFyiRef = useRef(false);
	useEffect(() => {
		if (showFyi) {
			hasShownFyiRef.current = true;
		}
	}, [showFyi]);

	const handleContentChangeWithData = useCallback(
		(_hasChanges: boolean, adf?: DocNode) => {
			onAgentMentionContentChange(adf);
		},
		[onAgentMentionContentChange],
	);

	const [{ searchSessionId }] = useSearchSessionId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { addReplyToCommentThread } = useCommentsDataActions();
	const [
		{ replyToCommentId, location, replyToCommentUser, replyToAgentUser },
		{ setReplyToCommentId },
	] = useCommentsPanel();
	const { unreadCommentIDSet } = useUnreadCommentsState();
	const isOffline = useAnnotationsIsOffline();
	const { commentService } = useCommentService();
	const [contentType] = useContentType();

	const allReplies = useMemo(
		() =>
			parentComment.replies.filter(
				(r) =>
					r !== null &&
					(parentComment.location as CommentLocation).inlineMarkerRef ===
						(r.location as CommentLocation).inlineMarkerRef,
			),
		[parentComment.replies, parentComment.location],
	);

	const numOfReplies = allReplies.length;

	const unreadReplyIds = useMemo(() => {
		return allReplies.filter((r) => unreadCommentIDSet.has(r.id)).map((r) => r.id);
	}, [allReplies, unreadCommentIDSet]);

	const [legacyVisibleReplies, setVisibleReplies] = useState<ReplyData[] | undefined>(undefined);
	const [, setCommentForEdit] = useState('');
	const [commentIsFocused, setCommentIsFocused] = useState<string | null>(null);

	const { showModal } = useDialogs();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const commentType = parentComment.type;
	const inlineCommentLocation = parentComment?.location as CommentLocation;
	const isDanglingComment =
		commentType === CommentType.INLINE &&
		inlineCommentLocation?.inlineResolveProperties?.isDangling &&
		!isStandaloneComment &&
		fg('confluence_frontend_new_dangling_comments_ux');
	const isParentCommentOpen = parentComment.isOpen;

	const isParentCommentResolvedOrDangling = !isParentCommentOpen || Boolean(isDanglingComment);

	const [currentReplyView, setReplyView] = useState(
		// Determine the reply view by counting the total number of top-level replies to the parent comment.
		// This excludes any nested replies.
		getDefaultReplyView(
			getCommentReplies(allReplies, parentCommentId).length,
			isParentCommentResolvedOrDangling,
			isStandaloneComment,
		),
	);

	// User page permissions
	const operations = pageInfo?.operations || [];

	// User space permissions - only access when feature gate is on
	const spaceOperations = fg('confluence_comments_space_operations_permissions')
		? pageInfo?.space?.operations || []
		: [];

	const canAddComment = operations.some(
		(op) => op?.operation === 'create' && op?.targetType === 'comment',
	);

	const canAddAttachment =
		operations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment') ||
		spaceOperations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment');

	// The user can upload media only if they have update permissions for the page if confluence_comment_use_comment_media_endpoint FG is off
	const hasMediaUploadPermissions = fg('confluence_comment_use_comment_media_endpoint')
		? canAddComment && canAddAttachment
		: operations.some((op) => op?.operation === 'update' && op?.targetType === pageType);

	const threadKey =
		commentType === CommentType.INLINE
			? (parentComment?.location as CommentLocation).inlineMarkerRef || ''
			: parentCommentId;

	// When in summarizer popup, force empty actions on replies.
	// reopen won't be rendered for reply, but we are using it to render disabled reaction summary for replies
	//
	// When the confluence_comments_panel_memo_rows experiment is enabled,
	// memoise supportedTopLevelActions so the array literal is stable across
	// renders for a given (isCommentSummaryPopup, isParentCommentOpen) pair.
	// Without this, this `let`-reassigned array is fresh on every render and
	// flows down to every reply <Comment> as a changed prop, defeating the
	// React.memo wrapper. When the experiment is disabled, fall back to the
	// legacy inline branch so behaviour is unchanged.
	const memoisedSupportedTopLevelActions = useMemo<CommentAction[]>(() => {
		if (isCommentSummaryPopup) {
			return [];
		}
		if (isParentCommentOpen) {
			return ['edit', 'resolve', 'delete'];
		}
		return ['reopen'];
	}, [isCommentSummaryPopup, isParentCommentOpen]);

	let supportedTopLevelActions: CommentAction[];
	if (expValEquals('confluence_comments_panel_memo_rows', 'isEnabled', true)) {
		supportedTopLevelActions = memoisedSupportedTopLevelActions;
	} else if (isCommentSummaryPopup) {
		supportedTopLevelActions = [];
	} else if (isParentCommentOpen) {
		supportedTopLevelActions = ['edit', 'resolve', 'delete'];
	} else {
		supportedTopLevelActions = ['reopen'];
	}

	// When inside the summarizer popup, never expand the reply editor inline.
	let expandReplyEditor = false;
	if (isCommentSummaryPopup) {
		expandReplyEditor = false;
	} else if (expValEquals('confluence_comments_tuneup', 'isEnabled', true)) {
		expandReplyEditor = isEditorExpanded;
	} else {
		expandReplyEditor =
			location === LocationValue.PANEL &&
			(replyToCommentId === parentCommentId ||
				(allReplies.some((reply) => reply.id === replyToCommentId) &&
					expValEquals('confluence_comment_expand_reply_editor', 'isEnabled', true)));
	}

	// Track if we've already fired the analytics event for current visibleReplies state
	const hasFiredForVisibleRepliesRef = useRef<string>('');

	const topLevelReplyToNestedChildrenMap = useMemo(() => {
		const topLevelReplyIds = new Set(
			allReplies.filter((reply) => reply.parentId === parentCommentId).map((reply) => reply.id),
		);

		// We need to get all replies that are nested to any depth under a top level reply.
		// This will help us render the GeneralReplyScroller component for each reply (mainly, we need to show user icons and the blue dot indicator)
		const topLevelReplyToNestedChildrenMapInternal: {
			[key: string]: ReplyData[];
		} = Array.from(topLevelReplyIds).reduce<{
			[key: string]: ReplyData[];
		}>((acc, replyId) => {
			acc[replyId] = [];
			return acc;
		}, {});
		const replyIdToReplyMap = allReplies.reduce<{
			[key: string]: ReplyData;
		}>((acc, reply) => {
			acc[reply.id] = reply;
			return acc;
		}, {});

		// Map all replies to their top level parent. Replies can be nested to any depth, so we need to traverse the tree.
		const filteredReplies = allReplies.filter((reply) => !topLevelReplyIds.has(reply.id));

		for (const filteredReply of filteredReplies) {
			let replyParentId = filteredReply.parentId;

			// Add a circuit breaker to prevent infinite loops if we get really wonky data
			let circuitBreaker = 0;
			while (replyParentId && circuitBreaker < 100 && !topLevelReplyIds.has(replyParentId)) {
				replyParentId = replyIdToReplyMap[replyParentId]?.parentId;
				circuitBreaker++;
			}

			if (replyParentId && replyParentId in topLevelReplyToNestedChildrenMapInternal) {
				// Second condition will always be true, unless we somehow hit circuit breaker above
				topLevelReplyToNestedChildrenMapInternal[replyParentId].push(filteredReply);
			}
		}

		return topLevelReplyToNestedChildrenMapInternal;
	}, [allReplies, parentCommentId]);

	// Determine the latest top level replies to show on the thread's initial load
	const latestReplies = useMemo(() => {
		const replies: ReplyData[] = [];
		const topLevelReplies = allReplies.filter(
			(reply) => reply.id in topLevelReplyToNestedChildrenMap,
		);

		// Sort by createdAtNonLocalized value
		topLevelReplies.sort((a, b) => {
			// Safely handle missing or invalid dates
			const dateA = getValidDate(a?.createdAtNonLocalized);
			const dateB = getValidDate(b?.createdAtNonLocalized);

			return dateA.getTime() - dateB.getTime();
		});

		// Add the latest replies to the replies array
		for (
			let i = 0;
			i <
			getDefaultNumVisibleReplies(
				numOfReplies,
				isStandaloneComment,
				isParentCommentResolvedOrDangling,
			);
			i++
		) {
			if (topLevelReplies.length > 0) {
				const reply = topLevelReplies.pop();
				if (reply) {
					// Unshift to maintain reply chronological order
					replies.unshift(reply);
				}
			}
		}

		return replies;
	}, [
		allReplies,
		isStandaloneComment,
		numOfReplies,
		topLevelReplyToNestedChildrenMap,
		isParentCommentResolvedOrDangling,
	]);

	/*
	 * Determine the latest top level reply IDs and their nested reply IDs.
	 * This is passed to RepliesToggler to determine the hidden replies.
	 */
	const latestReplyIds = useMemo(() => {
		const latestAndNestedReplyIds: Set<string> = new Set();

		for (const reply of latestReplies) {
			latestAndNestedReplyIds.add(reply.id);

			for (const nestedReply of topLevelReplyToNestedChildrenMap[reply.id]) {
				latestAndNestedReplyIds.add(nestedReply.id);
			}
		}

		return latestAndNestedReplyIds;
	}, [latestReplies, topLevelReplyToNestedChildrenMap]);

	// Wrapper for setVisibleReplies to match the expected type signature
	const setVisibleRepliesWrapper = useCallback<React.Dispatch<React.SetStateAction<ReplyData[]>>>(
		(value) => {
			if (typeof value === 'function') {
				setVisibleReplies((prev) => {
					const prevArray = prev === undefined ? [] : prev;
					return value(prevArray);
				});
			} else {
				setVisibleReplies(value);
			}
		},
		[],
	);

	useUpdateVisibleReplies({
		allReplies,
		isParentCommentOpen,
		currentReplyView,
		latestReplies,
		parentCommentId,
		setVisibleReplies: setVisibleRepliesWrapper,
	});

	// Render-time derivation of visible replies. Computed unconditionally to
	// satisfy the rules of hooks; the experiment gate below selects which value
	// is consumed. Computing during render (rather than syncing state in a
	// layout effect) lets visible replies appear in SSR HTML and removes a
	// post-mount re-render on the client.
	const derivedVisibleReplies = useVisibleReplies({
		allReplies,
		isParentCommentOpen,
		currentReplyView,
		latestReplies,
		parentCommentId,
	});

	const visibleReplies = expValEquals('confluence_fe_ssr_top_footer_comments', 'isEnabled', true)
		? derivedVisibleReplies
		: legacyVisibleReplies;

	// Track replies rendered state in experience tracker
	// Extract visibleReplies length to avoid complex expression in dependency array
	const visibleRepliesLength = visibleReplies?.length ?? -1;

	useEffect(() => {
		if (visibleReplies === undefined) {
			return;
		}

		const repliesRendered = currentReplyView === ViewReplyOptions.ALL && visibleReplies.length > 0;

		// Fire GASV3 event only when in ALL view with replies and we haven't fired for this state yet
		if (currentReplyView === ViewReplyOptions.ALL && visibleReplies.length > 0) {
			const stateKey = `${currentReplyView}-${visibleReplies.length}`;

			// Only fire if we haven't already fired for this exact state
			if (hasFiredForVisibleRepliesRef.current !== stateKey && numOfReplies !== 0) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'repliesRenderedStateChanged',
						actionSubject: 'comment',
						actionSubjectId: parentCommentId,
						objectType: pageType,
						objectId: pageId,
						source: isStandaloneComment
							? AnalyticsSource.STANDALONE
							: AnalyticsSource.COMMENTS_PANEL,
						attributes: {
							commentType: commentType === CommentType.GENERAL ? 'page' : 'inline',
							repliesRendered,
							currentReplyView,
							numOfReplies,
							visibleRepliesCount: visibleReplies.length,
						},
					},
				}).fire();

				// Mark this state as fired
				hasFiredForVisibleRepliesRef.current = stateKey;
			}
		}
	}, [
		currentReplyView,
		visibleReplies,
		visibleRepliesLength,
		numOfReplies,
		commentType,
		experienceTracker,
		createAnalyticsEvent,
		parentCommentId,
		pageType,
		pageId,
		isStandaloneComment,
	]);

	const removeAnnotationHighlight = useCallback(() => {
		if (pageMode === PageMode.VIEW) {
			eventEmitter?.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
		} else {
			eventEmitter?.emit('setselectedannotation', null);
		}
	}, [pageMode, eventEmitter]);

	// Track whether this CommentReplies is inside the comment summary popup.
	const isInsidePopupRef = useRef(isCommentSummaryPopup);
	isInsidePopupRef.current = isCommentSummaryPopup;

	// Reset ref on component unmount
	useEffect(() => {
		return () => {
			hasFiredForVisibleRepliesRef.current = '';

			// When inside the popup, skip resetting the panel's reply state on unmount.
			if (isInsidePopupRef.current) {
				return;
			}

			//TODO: Remove once https://product-fabric.atlassian.net/browse/COMMENTS-5857 is resolved
			const isDatabaseOrWhiteboard = contentType === 'database' || isCanvasContentType(contentType);
			if (
				!isDatabaseOrWhiteboard &&
				expValEquals('confluence_comment_reset_reply_options', 'isEnabled', true)
			) {
				setReplyToCommentId();
				removeAnnotationHighlight();
			}
		};
	}, [removeAnnotationHighlight, setReplyToCommentId, contentType]);

	const handleReplyButtonClick = () => {
		// When inside the summarizer popup, delegate to the popup's reply handler
		// so it can explicitly close the popup and then open the reply editor in the panel.
		// Check for unsaved draft first so the user can choose to keep it.
		if (isCommentSummaryPopup && replyToComment) {
			if (hasContentChanged) {
				showModal(CommentWarningDialog, {
					onConfirm: () => {
						resetContentChanged();
						replyToComment(parentCommentId, parentComment);
					},
				});
			} else {
				replyToComment(parentCommentId, parentComment);
			}
			return;
		}

		// Skip if there is no active inline comment annotation and currentlySelectedComment is not general
		const shouldRemoveAnnotationHighlight =
			commentType === CommentType.GENERAL && selectedAnnotation.id;

		if (hasContentChanged) {
			// if the content has changed and the user has clicked reply button to another comment thread, warn the user
			showModal(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					setReplyToCommentId({ parentCommentId, location: LocationValue.PANEL });
					shouldRemoveAnnotationHighlight && eventEmitter && removeAnnotationHighlight();
				},
			});
		} else {
			setReplyToCommentId({ parentCommentId, location: LocationValue.PANEL });
			shouldRemoveAnnotationHighlight && eventEmitter && removeAnnotationHighlight();
		}
	};

	const handleFocus = useCallback(
		(event: React.FocusEvent, replyId: string) => {
			event.stopPropagation();
			setCommentIsFocused(replyId);
		},
		[setCommentIsFocused],
	);

	const handleBlur = useCallback(
		(event: React.FocusEvent) => {
			if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget as Node)) {
				setCommentIsFocused(null);
			}
		},
		[setCommentIsFocused],
	);

	const renderReplyButton = () => {
		return (
			isParentCommentOpen &&
			canAddComments && (
				<WithCurvedBranchingStyle
					showDefaultStyles
					customStyles={replyActionButtonStyles}
					testId="comments-panel-reply-comment-action"
					onClick={handleReplyButtonClick}
				>
					<Button
						data-cy="comments-panel-reply-comment-action"
						css={customizedReplyButtonStyles}
						appearance="subtle"
						isDisabled={!isParentCommentOpen}
						analyticsContext={{
							attributes: {
								name: 'replyToCommentButton',
								source: AnalyticsSource.COMMENTS_PANEL,
							},
						}}
						interactionName="start-reply-comment-action"
						aria-label={formatMessage(i18n.threadReplyButtonAriaLabelWithName, {
							name: parentComment.author?.displayName || formatMessage(commentsi18n.anonymous),
						})}
					>
						<FormattedMessage {...i18n.replyAction} />
					</Button>
				</WithCurvedBranchingStyle>
			)
		);
	};

	// Helper function to check if the reply editor is visible in the comments panel
	const isReplyEditorVisible = () => {
		if (typeof document === 'undefined') {
			return false;
		}
		// Use combined selector to find the specific reply editor for this comment thread
		const replyEditorContainer = document.querySelector(
			`[data-testid="comment-thread-${parentCommentId}-replies"] [data-testid="comments-panel-new-reply-container"]`,
		);
		const panelContainer = document.querySelector('[data-testid="comments-panel-list-container"]');

		if (!replyEditorContainer || !panelContainer) {
			return false;
		}

		const replyEditorRect = replyEditorContainer.getBoundingClientRect();
		const panelRect = panelContainer.getBoundingClientRect();

		// Check if the bottom of the reply editor container is visible within the panel
		return replyEditorRect.bottom <= panelRect.bottom;
	};

	const editorChangeHandler = () => {
		let experienceName = REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE;

		if (commentType === CommentType.GENERAL) {
			experienceName = REPLY_TO_PAGE_COMMENT_LOAD_EXPERIENCE;
		}

		experienceTracker.succeed({
			name: experienceName,
		});

		// Check if we need to scroll to show the reply editor
		// Only scroll if the reply editor is for this specific comment thread
		if (
			replyToCommentId === parentCommentId &&
			!isReplyEditorVisible() &&
			expValEquals('confluence_comment_bring_replyeditor_intoview', 'isEnabled', true)
		) {
			// Use combined selector to find the specific reply editor for this comment thread
			const replyEditorContainer =
				typeof document !== 'undefined'
					? document.querySelector(
							`[data-testid="comment-thread-${parentCommentId}-replies"] [data-testid="comments-panel-new-reply-container"]`,
						)
					: null;
			if (replyEditorContainer) {
				replyEditorContainer.scrollIntoView({ behavior: 'smooth', block: 'end' });
			}
		}
	};

	// Wrap the createInlineReplyFn mutation with the interop logic that handles both the direct
	// Apollo mutation path and the new hook-based path for the Comments Data Manager
	const replyCreationInteropWrapper = useCallback(
		(
			options: MutationFunctionInteropSupportedOptions<
				CommentRepliesCreateInlineReplyMutation$data,
				CommentRepliesCreateInlineReplyMutation$variables
			>,
			onCacheUpdateError: (error: Error) => void,
		) =>
			createInlineReplyInterop<
				CommentRepliesCreateInlineReplyMutation$data,
				CommentRepliesCreateInlineReplyMutation$variables
			>({
				originalMutationFn: createInlineReplyFn,
				options,
				onCacheUpdateError,
			}),
		[createInlineReplyInterop, createInlineReplyFn],
	);

	// We need to intercept the raw mutation result to extract the new comment ID for agent mention
	// tagging. We can't chain .then() on onSaveReply() directly because onSaveReply's internal
	// .then() consumes the mutation data and returns undefined. Instead, we wrap the mutation fn
	// before passing it in, so we can tap the result before onSaveReply processes it.
	const buildAgentMentionCreateInlineReplyFn = (adf: any): SaveProps['createInlineReplyFn'] =>
		expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)
			? (...args: Parameters<NonNullable<SaveProps['createInlineReplyFn']>>) => {
					const promise = (replyCreationInteropWrapper as SaveProps['createInlineReplyFn'])!(
						...args,
					);
					void promise?.then(({ data }: any) => {
						const commentId = data?.replyInlineComment?.id ?? parentCommentId;
						handleAgentMentionInComment(adf, cloudId, pageId, commentId, userId ?? undefined);
					});
					return promise;
				}
			: (replyCreationInteropWrapper as SaveProps['createInlineReplyFn']);

	// Same pattern as buildAgentMentionCreateInlineReplyFn but for footer/general comments.
	const buildAgentMentionCreateGeneralReplyFn = (adf: any): SaveProps['createGeneralReplyFn'] =>
		expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)
			? (...args: Parameters<NonNullable<SaveProps['createGeneralReplyFn']>>) => {
					const promise = (createGeneralReplyFn as SaveProps['createGeneralReplyFn'])!(...args);
					void promise?.then(({ data }: any) => {
						const commentId = data?.createFooterComment?.id ?? parentCommentId;
						handleAgentMentionInComment(adf, cloudId, pageId, commentId, userId ?? undefined);
					});
					return promise;
				}
			: (createGeneralReplyFn as SaveProps['createGeneralReplyFn']);

	const renderCommentEditor = () => {
		let customBranchingStyles = commentEditorLegacyCustomStyles;
		if (fg('confluence_frontend_content_wrapper')) {
			// Accounts for extra padding in the author (was previously handled in a higher parent component)
			customBranchingStyles = commentEditorCustomStyles;
		}

		const editor = (
			<CommentEditor
				pageId={pageId}
				pageType={pageType}
				content={getReplyEditorContent({ replyToCommentUser, replyToAgentUser })}
				appearance="chromeless"
				EditorComponent={ChromelessEditor}
				onSaveComment={(adf, onSuccess) => {
					return onSaveReply({
						pageId,
						parentCommentId,
						cloudId,
						adf,
						onSuccess,
						pageMode,
						editCommentQueryId,
						setCommentForEdit,
						createAnalyticsEvent,
						pageType,
						experienceTracker,
						createInlineReplyFn: buildAgentMentionCreateInlineReplyFn(adf),
						createGeneralReplyFn: buildAgentMentionCreateGeneralReplyFn(adf),
						createObjectReplyFn: expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
							? createObjectCommentMutationFn
							: undefined,
						addCommentInCommentsDataManagerCacheFn: expVal(
							'cc_comments_new_comments_data_manager',
							'isEnabled',
							false,
						)
							? addCommentInCache
							: undefined,
						setReplyToCommentId,
						resetContentChanged,
						setReplyView,
						selectedAnnotation,
						addReplyToCommentThread: expVal(
							'cc_comments_new_comments_data_manager',
							'isEnabled',
							false,
						)
							? () => {}
							: addReplyToCommentThread,
						threadKey,
						commentType,
						searchSessionId,
						isReactionsEnabled,
						analyticsSource: isStandaloneComment
							? AnalyticsSource.STANDALONE
							: AnalyticsSource.COMMENTS_PANEL,
						contentTypesCommentService: commentService,
						userId,
						displayName,
					});
				}}
				onContentChange={onChange}
				onContentChangeWithData={handleContentChangeWithData}
				onEditorReady={editorChangeHandler}
				commentMode="reply"
				commentType={commentType === CommentType.GENERAL ? 'page' : 'inline'}
				spaceId=""
				showCancelButton
				useNewWarningModal
				hideWatchCheckbox
				expandEditor={expandReplyEditor}
				pageMode="view"
				hasMediaUploadPermissions={hasMediaUploadPermissions}
				topLevelCommentId={parentComment.id}
				shouldWarnOnInternalNavigation
				onCancelComment={({
					destroy = false,
				}: {
					destroy?: boolean;
				} = {}) => {
					// when the transition happens the
					// other reply list item is clicked
					// and we need to reset ssetReplyToCommentId
					if (!destroy || replyToCommentId !== parentCommentId) {
						setReplyToCommentId();
					}
				}}
				isCommentsPanel
				wasRemovedByAnotherUser={parentComment.wasRemovedByAnotherUser}
				isOffline={isOffline}
			/>
		);

		const editorWithFyi = (
			<>
				{(showFyi || hasShownFyiRef.current) &&
					expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) && (
						<AgentMentionFyiNudge visible={showFyi} agent={agent} />
					)}
				{editor}
			</>
		);

		return (
			<NewReplyContainer
				mode="view"
				isCommentsPanel
				isFabricPage
				data-testid="comments-panel-new-reply-container"
			>
				{expValEquals('confluence_comments_tuneup', 'isEnabled', true) ? (
					editorWithFyi
				) : (
					<WithCurvedBranchingStyle showDefaultStyles customStyles={customBranchingStyles}>
						{editorWithFyi}
					</WithCurvedBranchingStyle>
				)}
			</NewReplyContainer>
		);
	};

	const renderUpdatedLowerAction = () => {
		if (expandReplyEditor) {
			return renderCommentEditor();
		} else if (!isStandaloneComment) {
			return renderReplyButton();
		} else {
			return null;
		}
	};

	// Make reply containers tabbable for accessibility
	useEffect(() => {
		if (visibleReplies && visibleReplies.length > 0) {
			// Find all reply containers and make them tabbable
			visibleReplies.forEach((reply) => {
				const replyContainer = document.querySelector(
					`[data-testid="comment-thread-${parentComment.id}-replies-${reply.id}"]`,
				);

				if (replyContainer instanceof HTMLElement) {
					replyContainer.setAttribute('tabIndex', '0');
				}
			});
		}
	}, [visibleReplies, parentComment.id]);

	const renderRepliesToggler = () => (
		<RepliesToggler
			replies={allReplies}
			unreadReplyIds={unreadReplyIds}
			setReplyView={setReplyView}
			currentReplyView={currentReplyView}
			commentType={commentType}
			parentCommentId={parentCommentId}
			isParentOpen={isParentCommentOpen}
			canAddComments={canAddComments}
			latestReplyIds={latestReplyIds}
			isStandaloneComment={isStandaloneComment}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			analyticsSource={
				isStandaloneComment ? AnalyticsSource.STANDALONE : AnalyticsSource.COMMENTS_PANEL
			}
			expandReplyEditor={expandReplyEditor}
		/>
	);

	return (
		<>
			{numOfReplies > 0 &&
				expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
				renderRepliesToggler()}
			<div
				data-testid={`comment-thread-${parentComment.id}-replies`}
				css={[
					commentThreadRepliesBoxStyles,
					expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
						commentThreadRepliesBoxStylesV2,
					parentComment.wasRemovedByAnotherUser && translucentStyle,
					isStandaloneComment && commentThreadRepliesBoxStandaloneStyleOverrides,
					isStandaloneComment &&
						expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
						commentThreadRepliesBoxStandaloneStyleOverridesV2,
				]}
			>
				<div>
					{numOfReplies > 0 &&
						!expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
						renderRepliesToggler()}
					{visibleReplies && visibleReplies.length > 0 && (
						<div data-testid={`comment-thread-${parentComment.id}-reply-list`}>
							{visibleReplies.map((reply, idx) => {
								const isLastUnnestableReply = idx === visibleReplies.length - 1;

								// Keep the connector line visible when this reply has an agent-session status
								// row below it. Gate inlined, ordered last per feature-gating rules.
								const replyHasAgentSession =
									Boolean(agentSessions.get(reply.id)) &&
									expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true);

								return (
									// eslint-disable-next-line @atlassian/a11y/no-static-element-interactions, @atlassian/a11y/no-noninteractive-element-interactions
									<div
										css={[
											replyBoxStyles,
											fg('confluence_frontend_content_wrapper') &&
												commentHandlesPaddingReplyBoxOverrides,
										]}
										key={`comment-thread-${parentComment.id}-replies-${idx}`}
										data-testid={`comment-thread-${parentComment.id}-replies-${reply.id}`}
										onMouseEnter={() => handleMouseEnter(reply.id)}
										onMouseLeave={handleMouseLeave}
										onFocus={(event) => handleFocus(event, reply.id)}
										onBlur={handleBlur}
										aria-label={formatMessage(i18n.replyAriaLabel, {
											authorName:
												reply.author?.displayName || formatMessage(commentsi18n.anonymous),
											timestamp: formatTimestampForAriaLabel({
												intl,
												dateString: reply.createdAtNonLocalized,
												isReply: true,
											}),
										})}
									>
										<Comment
											flags={flags}
											comment={reply}
											supportedTopLevelActions={supportedTopLevelActions}
											hideReactions={hideReactions}
											pageInfo={pageInfo}
											pageMode={pageMode}
											isUnread={unreadCommentIDSet.has(reply.id)}
											isHovered={
												expValEquals(
													'confluence_frontend_fix_comment_actions_visibility',
													'isEnabled',
													true,
												)
													? false
													: hoveredCommentId === reply.id || commentIsFocused === reply.id
											}
											threadKey={threadKey}
											// When the parent comment is resolved, hide the styles for the last reply —
											// but keep them when the reply has an agent-session row so the line reaches it.
											hideBranchingStyle={
												!replyHasAgentSession &&
												((!canAddComments && isLastUnnestableReply) ||
													(isStandaloneComment && isLastUnnestableReply) ||
													(isLastUnnestableReply &&
														!expandReplyEditor &&
														expValEquals('confluence_comments_tuneup', 'isEnabled', true)))
											}
											adjustForNestedReplies={
												commentType === CommentType.GENERAL &&
												reply.id in topLevelReplyToNestedChildrenMap &&
												topLevelReplyToNestedChildrenMap[reply.id]?.length > 0 &&
												fg('confluence_frontend_content_wrapper')
											}
											canAddComments={canAddComments}
											isParentCommentOpen={isParentCommentOpen}
											deleteStandaloneComment={deleteStandaloneComment}
											isStandaloneComment={isStandaloneComment}
											replyToComment={replyToComment}
											updateReactionsCacheFn={updateReactionsCacheFn}
											onRendered={onRendered}
											isLastUnnestableReply={isLastUnnestableReply}
											isReplyEditorOpen={expandReplyEditor}
											commentHandlesPadding={fg('confluence_frontend_content_wrapper')}
											hasParentMaxHeight={hasParentMaxHeight}
										/>
										{commentType === CommentType.GENERAL &&
											reply.id in topLevelReplyToNestedChildrenMap &&
											topLevelReplyToNestedChildrenMap[reply.id]?.length > 0 &&
											fg('confluence_frontend_content_wrapper') && (
												<GeneralReplyScroller
													nestedReplies={topLevelReplyToNestedChildrenMap[reply.id]}
													unreadReplyIds={unreadReplyIds}
													targetCommentIdForScroll={reply.id}
													isLastUnnestableReply={isLastUnnestableReply}
												/>
											)}
									</div>
								);
							})}
						</div>
					)}
				</div>
				{expValEquals('confluence_comments_tuneup', 'isEnabled', true) ? (
					<RepliesFooter
						replyView={currentReplyView}
						setReplyView={setReplyView}
						isEditorExpanded={!isCommentSummaryPopup && isEditorExpanded}
						isParentCommentOpen={isParentCommentOpen}
						isStandaloneComment={isStandaloneComment}
						handleReplyButtonClick={handleReplyButtonClick}
						numOfVisibleReplies={visibleReplies ? visibleReplies.length : 0}
						isOffline={isOffline}
						scrollToId={threadKey}
						location={LocationValue.PANEL}
					>
						{renderCommentEditor()}
					</RepliesFooter>
				) : (
					renderUpdatedLowerAction()
				)}
			</div>
		</>
	);
};

// Wrap with React.memo gated by the comments-panel-memo-rows experiment.
// When the experiment is enabled, shallow-equal props will skip a re-render
// of the replies subtree (which itself contains many Comment subtrees) during
// unrelated parent re-renders. When disabled, the equality function returns
// false and behaviour matches the pre-experiment implementation.
export const CommentReplies: typeof CommentRepliesComponent = memo(
	CommentRepliesComponent,
	arePropsShallowEqualWhenMemoRowsEnabled,
) as typeof CommentRepliesComponent;
